/**
 * Agency console client — talks to the /agency and /agency-admin mounts of
 * job-apply-service (Fase 3). Same shape as admin/api/adminClient.ts.
 *
 * Two things this file owns that the admin client does not need:
 *
 * 1. `X-Agency-Id`. A person can hold seats in several agencies; the backend
 *    pins one per request from this header. It is persisted in localStorage so
 *    the switcher survives a reload — losing it would silently move a recruiter
 *    back to their first agency mid-task.
 * 2. `AgencyApiError`. The screens need the STATUS to tell "you have no seat"
 *    (401/403 on /api/me) apart from "the server broke", and the 409s that carry
 *    the current status of a match, so a bare `Error(message)` is not enough.
 */
import { apiFetch } from '~/lib/api';
import type { TermsAcceptanceBody, TermsStatus } from '~/lib/termsVersions';
import type { AgencyEnglishAssessment, RecordingUrl } from '~/lib/englishAssessment';

const AGENCY_ID_KEY = 'tbit.job-apply.agency_id';

export class AgencyApiError extends Error {
  status: number;
  code?: string;
  body: Record<string, any>;

  constructor(status: number, body: Record<string, any>) {
    super(body?.error || `HTTP ${status}`);
    this.name = 'AgencyApiError';
    this.status = status;
    this.body = body ?? {};
    this.code = typeof body?.code === 'string' ? body.code : undefined;
  }
}

export function getActiveAgencyId(): string | null {
  try {
    return window.localStorage.getItem(AGENCY_ID_KEY);
  } catch {
    // Private-mode Safari throws on localStorage. A recruiter with one seat
    // never needs the header, so failing soft is better than failing the page.
    return null;
  }
}

export function setActiveAgencyId(agencyId: string | null) {
  try {
    if (agencyId) window.localStorage.setItem(AGENCY_ID_KEY, agencyId);
    else window.localStorage.removeItem(AGENCY_ID_KEY);
  } catch {
    /* ignore */
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'content-type': 'application/json',
    ...((init?.headers as Record<string, string>) || {}),
  };
  const agencyId = getActiveAgencyId();
  if (agencyId) headers['X-Agency-Id'] = agencyId;

  const res = await apiFetch(path, { ...init, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new AgencyApiError(res.status, body);
  }
  return res.json() as Promise<T>;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type AgencyMemberRole = 'admin' | 'recruiter';
export type AgencyStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED';
export type AgencyPositionStatus = 'open' | 'paused' | 'filled';

/**
 * El vocabulario cerrado del backend (`lib/jobTypes.ts`). Se repite acá y no se
 * importa porque son dos repos: la lista está congelada del lado del servidor
 * con un enum de mongoose, así que un valor inventado desde el cliente vuelve
 * como 400 y no como una fila silenciosamente mal guardada.
 */
export const JOB_TYPES = ['full_time', 'part_time', 'seasonal', 'temporary', 'contract'] as const;
export type JobType = (typeof JOB_TYPES)[number];
export type AgencyMatchStatus =
  | 'suggested'
  | 'shortlisted'
  | 'contact_requested'
  | 'candidate_accepted'
  | 'candidate_declined'
  | 'dismissed';

export interface AgencyMe {
  email: string;
  member: { _id: string; name: string | null; role: AgencyMemberRole };
  agency: {
    _id: string;
    name: string;
    status: AgencyStatus;
    contact_email: string;
    country: string | null;
    website?: string | null;
    access_requested_at?: string;
    approved_at?: string;
    can_view_candidates: boolean;
    /**
     * Si la agencia aceptó la versión vigente de los Términos de TBit Jobs.
     * Sin eso, pedir acceso trae la casilla. Ausente en un backend anterior al
     * 2026-10-08.
     */
    terms?: TermsStatus;
  };
  agencies: Array<{
    _id: string;
    name: string;
    status: AgencyStatus;
    country: string | null;
    role: AgencyMemberRole | null;
  }>;
  /** Lo que el servicio prende por agencia (flags). Ausente = todo apagado. */
  features?: { english_assessment?: boolean };
}

/** Un paquete de evaluaciones: el precio lo pone el servidor, el cliente solo manda el id. */
export interface CreditPack {
  id: string;
  credits: number;
  amount_cents: number;
  currency: 'usd';
}

export type CreditEntryType = 'grant_free' | 'purchase' | 'reserve' | 'consume' | 'release' | 'adjust' | 'refund';

export interface CreditEntry {
  _id: string;
  type: CreditEntryType;
  credits: number;
  at: string;
  pack: string | null;
  amount_cents: number | null;
  currency: string | null;
  assessment_id: string | null;
  note: string | null;
}

/** `GET /agency/api/assessment-credits`. */
export interface AssessmentCredits {
  /** false = el cobro está apagado (kill switch): pedir es gratis. */
  enabled: boolean;
  balance: number;
  /** Reservas abiertas (pedidas, esperando la llamada). */
  reserved: number;
  free_credits: number;
  free_granted: boolean;
  packs: CreditPack[];
  history: CreditEntry[];
  terms?: TermsStatus;
}

export interface AgencyPosition {
  _id: string;
  title: string;
  company?: string;
  location: string;
  description?: string;
  requirements?: string;
  duties?: string;
  special_requirements?: string;
  contact_email?: string;
  contact_phone?: string;
  application_url?: string;
  wage_from?: number;
  wage_to?: number;
  wage_per?: string;
  workers_needed?: number;
  begin_date?: string;
  end_date?: string;
  housing_provided?: boolean;
  position_status?: AgencyPositionStatus;
  /** Ausente en las filas del DOL y en todo lo publicado antes del enum. */
  job_type?: JobType;
  created_at?: string;
  /** { suggested: 4, shortlisted: 1, … } — absent statuses simply are not keys. */
  match_counts?: Partial<Record<AgencyMatchStatus, number>>;
}

/**
 * A row of GET /agency/api/matches. `candidate_ref` is the candidate's
 * membership id as a string: it is what links a match to a suggestion, and it
 * is deliberately the ONLY identifier the agency ever sees. `contact` is
 * present exclusively on candidate_accepted — the backend removes the key on
 * every other status, and the UI re-checks (see lib/format.ts#visibleContact).
 */
export interface AgencyMatch {
  _id: string;
  position_id: string;
  status: AgencyMatchStatus;
  score: number;
  invited_at: string | null;
  responded_at: string | null;
  created_at?: string;
  candidate_ref: string;
  skills: string[];
  experience_years?: number;
  target_roles: string[];
  target_locations: string[];
  contact?: { name?: string | null; email?: string | null };
  /**
   * Sólo viene en la vista GLOBAL (sin `position_id`): ahí la fila tiene que
   * decir de qué vacante habla. Dentro de una posición la clave no llega,
   * porque la pantalla ya sabe en cuál está. `null` = borraron la posición.
   */
  position?: {
    _id: string;
    title: string;
    company?: string;
    location?: string;
    position_status?: AgencyPositionStatus;
    job_type?: JobType;
  } | null;
}

/** Los cinco números del resumen. Todos son conteos de filas que existen. */
export interface AgencyOverview {
  open_positions: number;
  suggested_new: number;
  shortlisted: number;
  contact_requested: number;
  accepted: number;
  /** La fecha de corte de `suggested_new`, para el subtítulo. */
  new_since: string;
}

export interface AgencyTeamMember {
  _id: string;
  email: string;
  name: string | null;
  role: AgencyMemberRole;
  /**
   * Se estampa en el primer login (`platform_owner_id`). Es la ÚNICA señal que
   * hay para distinguir un asiento activo de uno recién invitado: no existe
   * correo de invitación, la persona entra con el login de plataforma usando
   * ese mismo correo.
   */
  has_logged_in: boolean;
  created_at?: string;
}

/** A row of GET /agency/api/positions/:id/candidates — never carries contact data. */
export interface CandidateSuggestion {
  membership_id: string;
  cv_id?: string;
  score: number;
  profile_anonimizado: {
    first_name: string;
    last_initial: string;
    skills: string[];
    headline: string;
  };
}

export interface PositionInput {
  title?: string;
  company?: string;
  location?: string;
  description?: string;
  requirements?: string;
  duties?: string;
  special_requirements?: string;
  contact_email?: string;
  contact_phone?: string;
  application_url?: string;
  wage_from?: number | null;
  wage_to?: number | null;
  wage_per?: string;
  workers_needed?: number | null;
  begin_date?: string | null;
  end_date?: string | null;
  housing_provided?: boolean;
  position_status?: AgencyPositionStatus;
  /** `null` o `''` lo BORRA (el backend hace $unset); ausente lo deja como está. */
  job_type?: JobType | null;
}

export interface AgencyRegistrationInput extends TermsAcceptanceBody {
  name: string;
  country?: string;
  website?: string;
  contact_email?: string;
  /** Token del correo frío (`?c=`) con el que llegó, para atribuir el registro. */
  outreach_token?: string;
}

const BASE = '/agency/api';

export const agencyApi = {
  me: () => request<AgencyMe>(`${BASE}/me`),
  register: (body: AgencyRegistrationInput) =>
    request<AgencyMe>(`${BASE}/register`, { method: 'POST', body: JSON.stringify(body) }),
  /** Con `terms` cuando la agencia todavía no aceptó la versión vigente (la casilla del aviso). */
  requestAccess: (terms?: TermsAcceptanceBody) =>
    request<{ access_requested_at: string }>(`${BASE}/access-request`, {
      method: 'POST',
      ...(terms ? { body: JSON.stringify(terms) } : {}),
    }),
  overview: () => request<AgencyOverview>(`${BASE}/overview`),

  listTeam: () => request<{ members: AgencyTeamMember[] }>(`${BASE}/team`),
  inviteTeamMember: (body: { email: string; name?: string; role: AgencyMemberRole }) =>
    request<{ member: AgencyTeamMember }>(`${BASE}/team`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeTeamMember: (memberId: string) =>
    request<{ removed: string }>(`${BASE}/team/${memberId}`, { method: 'DELETE' }),

  listPositions: (params: URLSearchParams) =>
    request<{ page: number; total: number; positions: AgencyPosition[] }>(`${BASE}/positions?${params}`),
  getPosition: (id: string) =>
    request<{ position: AgencyPosition; match_counts: AgencyPosition['match_counts'] }>(`${BASE}/positions/${id}`),
  createPosition: (body: PositionInput) =>
    request<{ position: AgencyPosition; embedding_ready: boolean }>(`${BASE}/positions`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  patchPosition: (id: string, body: PositionInput) =>
    request<{ position: AgencyPosition; embedding_refreshed: boolean }>(`${BASE}/positions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /**
   * Runs the matcher. It is a GET, but it WRITES: the suggestions it returns are
   * upserted as matches, which is why the detail screen reloads the match list
   * right after calling it. 409 means the position has no embedding yet — not
   * that there are no candidates.
   */
  searchCandidates: (id: string, limit = 20) =>
    request<{ position_id: string; count: number; candidates: CandidateSuggestion[] }>(
      `${BASE}/positions/${id}/candidates?limit=${limit}`,
    ),

  listMatches: (params: URLSearchParams) =>
    request<{ page: number; matches: AgencyMatch[] }>(`${BASE}/matches?${params}`),
  shortlist: (id: string) =>
    request<{ match: AgencyMatch }>(`${BASE}/matches/${id}/shortlist`, { method: 'POST' }),
  dismiss: (id: string) =>
    request<{ match: AgencyMatch }>(`${BASE}/matches/${id}/dismiss`, { method: 'POST' }),
  requestContact: (id: string) =>
    request<{ match: AgencyMatch }>(`${BASE}/matches/${id}/request-contact`, { method: 'POST' }),

  /**
   * Evaluación de inglés por llamada. Detrás del flag del servicio: apagado,
   * las tres contestan 404 (y `me.features.english_assessment` es false, que es
   * lo que de verdad esconde la UI). Un 409 al pedirla trae la activa en
   * `body.assessment`.
   */
  listEnglishAssessments: (matchIds: string[]) =>
    request<{ assessments: AgencyEnglishAssessment[] }>(
      `${BASE}/english-assessments?match_ids=${matchIds.map(encodeURIComponent).join(',')}`,
    ),
  requestEnglishAssessment: (matchId: string) =>
    request<{ assessment: AgencyEnglishAssessment; reused: boolean }>(
      `${BASE}/matches/${matchId}/english-assessment`,
      { method: 'POST' },
    ),
  /**
   * Créditos prepagados de la evaluación (3 gratis, después paquetes). Mismas
   * puertas que la evaluación: con el flag apagado, 404.
   */
  assessmentCredits: () => request<AssessmentCredits>(`${BASE}/assessment-credits`),
  buyAssessmentCredits: (pack: string, locale: 'es' | 'en', terms?: TermsAcceptanceBody) =>
    request<{ url: string; id: string }>(`${BASE}/assessment-credits/checkout`, {
      method: 'POST',
      body: JSON.stringify({ pack, locale, ...(terms ?? {}) }),
    }),
  creditsCheckoutStatus: (sessionId: string) =>
    request<{ credited: boolean; credits: number | null; balance: number }>(
      `${BASE}/assessment-credits/checkout/${encodeURIComponent(sessionId)}`,
    ),
  cancelEnglishAssessment: (id: string) =>
    request<{ assessment: AgencyEnglishAssessment }>(`${BASE}/english-assessments/${id}/cancel`, {
      method: 'POST',
    }),
  /** URL firmada (minutos) de la grabación: solo para la agencia que pidió la evaluación. */
  englishRecordingUrl: (id: string) => request<RecordingUrl>(`${BASE}/english-assessments/${id}/recording`),
};
